/**
 * Phase B2 — Locale formatting (frontend single source of truth).
 *
 * Every visible date/time/currency in the app routes through here so that
 * a per-org locale flip on the backend takes effect everywhere on the next
 * page render — no per-file `toLocaleDateString` strings to chase down.
 *
 * Defaults to "en-GB" because EntryBook targets UK + IE customers. The
 * frontend's <LocaleContext> initialises from /api/auth/me so this module
 * stays pure (no React, no axios) and can be unit-tested in plain Node.
 *
 * Out of scope here (defer to a future phase):
 *   - Full i18n / translation framework (de-DE, fr-FR, …)
 *   - RTL (ar-SA etc.)
 */

export const ALLOWED_LOCALES = ["en-GB", "en-IE", "en-US"];
export const DEFAULT_LOCALE = "en-GB";

export const LOCALE_LABELS = {
  "en-GB": "English (United Kingdom)",
  "en-IE": "English (Ireland)",
  "en-US": "English (United States)",
};

const LOCALE_CURRENCY = {
  "en-GB": "GBP",
  "en-IE": "EUR",
  "en-US": "USD",
};

function _normalise(locale) {
  if (!locale) return DEFAULT_LOCALE;
  const s = String(locale).replace("_", "-");
  return ALLOWED_LOCALES.includes(s) ? s : DEFAULT_LOCALE;
}

function _toDate(d) {
  if (d instanceof Date) return Number.isNaN(d.getTime()) ? null : d;
  if (typeof d === "string" || typeof d === "number") {
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  return null;
}

// ---- date / time ----------------------------------------------------------

/** Returns DD/MM/YYYY for en-GB/en-IE, MM/DD/YYYY for en-US. */
export function formatDate(d, locale) {
  const dt = _toDate(d);
  if (!dt) return "—";
  const loc = _normalise(locale);
  return dt.toLocaleDateString(loc, { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** Returns "DD/MM/YYYY HH:MM" (24h) for UK/IE, "MM/DD/YYYY h:MM AM" (12h) for US. */
export function formatDateTime(d, locale) {
  const dt = _toDate(d);
  if (!dt) return "—";
  const loc = _normalise(locale);
  return dt.toLocaleString(loc, {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
    hour12: loc === "en-US",
  });
}

/** Returns "HH:MM" (24h) or "h:MM AM" (12h). */
export function formatTime(d, locale) {
  const dt = _toDate(d);
  if (!dt) return "—";
  const loc = _normalise(locale);
  return dt.toLocaleTimeString(loc, {
    hour: "2-digit", minute: "2-digit", hour12: loc === "en-US",
  });
}

/**
 * Returns "2 hours ago" / "in 30 minutes" using `Intl.RelativeTimeFormat`.
 * Falls back to formatDate() if Intl support is missing or the input is bad.
 */
export function formatRelative(d, locale) {
  const dt = _toDate(d);
  if (!dt) return "—";
  const loc = _normalise(locale);
  try {
    const rtf = new Intl.RelativeTimeFormat(loc, { numeric: "auto" });
    const diffMs = dt.getTime() - Date.now();
    const abs = Math.abs(diffMs);
    const MIN = 60_000;
    const HOUR = 3_600_000;
    const DAY = 86_400_000;
    const WEEK = 604_800_000;
    if (abs < MIN) return rtf.format(Math.round(diffMs / 1000), "second");
    if (abs < HOUR) return rtf.format(Math.round(diffMs / MIN), "minute");
    if (abs < DAY) return rtf.format(Math.round(diffMs / HOUR), "hour");
    if (abs < WEEK) return rtf.format(Math.round(diffMs / DAY), "day");
    return formatDate(dt, locale);
  } catch {
    return formatDate(dt, locale);
  }
}

// ---- currency -------------------------------------------------------------

/**
 * formatCurrency(amountMinor, currency, locale)
 *   - amountMinor: integer in the smallest unit (e.g. pence, cents). 2900 → £29.00
 *   - currency:    "GBP" | "EUR" | "USD" (ISO-4217)
 *   - locale:      viewer's locale — affects symbol position / thousands separators
 *                  but NOT the currency itself (a GBP price renders as £ for every viewer).
 *
 * The brief calls this out explicitly: Stripe products are priced in GBP,
 * so an en-US viewer sees "£29.00" not "$29.00". Currency is product-driven,
 * locale is viewer-driven.
 */
export function formatCurrency(amountMinor, currency, locale) {
  const cur = (currency || "GBP").toUpperCase();
  const loc = _normalise(locale);
  const major = Number(amountMinor) / 100;
  if (!Number.isFinite(major)) return "—";
  try {
    return new Intl.NumberFormat(loc, {
      style: "currency",
      currency: cur,
      currencyDisplay: "symbol",
    }).format(major);
  } catch {
    // Fallback to en-GB if currency is unknown — Intl throws on bad ISO codes.
    return new Intl.NumberFormat(DEFAULT_LOCALE, {
      style: "currency",
      currency: "GBP",
    }).format(major);
  }
}

export function getCurrencyForLocale(locale) {
  const loc = _normalise(locale);
  return LOCALE_CURRENCY[loc] || "GBP";
}

// ---- compat shim ----------------------------------------------------------

/**
 * Legacy DD/MM/YYYY helper. Previously declared inside `DateRangePicker.jsx`
 * (PR-2C close-out follow-up) — re-exported here so existing imports stay
 * working while we migrate the codebase to `formatDate(d, locale)`.
 */
export function fmtDateDMY(d) {
  return formatDate(d, "en-GB");
}
